import { useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '../../lib/trpc';
import { PageContainer } from '../../components/PageContainer';
import { PageLoading } from '../../components/ui/LoadingSpinner';
import { ErrorDisplay } from '../../components/ui/ErrorDisplay';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Button } from '../../components/ui/Button';
import { Typography } from '../../components/ui/Typography';
import { AdminListPagination } from './AdminListPagination';
import { reasonLabel } from './accountDeletionCopy';

const PAGE_SIZE = 20;

const headerClass = 'text-xs font-medium text-gray-500 uppercase tracking-wider';

export const AdminBannedEmails = () => {
  const utils = trpc.useUtils();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [unbanTarget, setUnbanTarget] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error, refetch } = trpc.adminUsers.bannedEmails.useQuery(
    {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      search: debouncedSearch || undefined,
    },
    { placeholderData: keepPreviousData }
  );

  const unban = trpc.adminUsers.unban.useMutation({
    onSuccess: () => {
      utils.adminUsers.bannedEmails.invalidate();
      utils.adminUsers.pendingDeletions.invalidate();
      utils.adminUsers.list.invalidate();
      utils.adminUsers.getById.invalidate();
      setUnbanTarget(null);
    },
    // Close the modal so the page-level error is not hidden behind its backdrop.
    onError: () => setUnbanTarget(null),
  });

  // Keep the search box mounted during refetches; only the first load blanks the page.
  if (isLoading) {
    return (
      <PageContainer>
        <PageLoading message="Loading banned emails..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorDisplay
          title="Failed to load banned emails"
          message={error.message}
          showRetry
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <Typography variant="heading1" as="h1">
          Banned emails
        </Typography>

        <div>
          <label htmlFor="banned-search" className="sr-only">
            Search banned emails
          </label>
          <input
            id="banned-search"
            type="search"
            value={search}
            placeholder="Search by email"
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none"
          />
        </div>

        {unban.error && (
          <p role="alert" className="text-sm text-error">
            {unban.error.message}
          </p>
        )}

        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-x-auto">
          {!data || data.items.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">No banned emails.</div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className={`px-4 py-3 ${headerClass}`}>Email</th>
                  <th className={`px-4 py-3 ${headerClass}`}>Reason</th>
                  <th className={`px-4 py-3 ${headerClass}`}>Banned by</th>
                  <th className={`px-4 py-3 ${headerClass}`}>Date</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.email} className="border-b border-gray-100 text-sm">
                    <td className="px-4 py-3 text-gray-900">{item.email}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{reasonLabel(item.reason)}</div>
                      {item.note && <div className="text-xs text-gray-500">{item.note}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{item.bannedBy?.email ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setUnbanTarget(item.email)}
                        >
                          Unban
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data && (
          <AdminListPagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={unbanTarget !== null}
        onClose={() => {
          if (!unban.isPending) setUnbanTarget(null);
        }}
        onConfirm={() => unbanTarget && unban.mutate({ email: unbanTarget })}
        title="Unban email"
        message={`${unbanTarget ?? ''} will be able to sign up and sign in again.`}
        confirmText="Unban"
        isLoading={unban.isPending}
      />
    </PageContainer>
  );
};
